import {
  useContext,
  createContext,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { api, errorTreatment } from "../../services/api";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { setCookie, parseCookies, destroyCookie } from "nookies";
import { LoadingContext } from "../Loading/LoadingContext";
interface Login {
  data: {
    account: string;
    token: string;
    email: string;
  };
}

interface LoginMemcache {
  data: {
    account_code: string;
    account_id: string;
    account_name: string;
    username: string;
    email: string;
    token: string;
  };
}

interface SignInCredentials {
  code: string;
  username: string;
  password: string;
}

interface AuthContextData {
  signIn(credentials: SignInCredentials): void;
  user: string;
  isAuthenticated: boolean;
  signOut: () => void;
  account: string;
  homeAcess: string;
  memcacheLogin: (token: string) => void;
  hasShortToken: boolean;
  getShortToken: (shortToken: string) => Promise<boolean>;
  loginErrorMessage: string;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext({} as AuthContextData);

export function AuthProvider({ children }: AuthProviderProps) {
  const { changeVisibility } = useContext(LoadingContext);
  const {
    "controlTower.token": token,
    "controlTower.username": usernm,
    "controlTower.account": accnt,
    "controlTower.homeAcess": homeacs,
  } = parseCookies();

  const navigate = useNavigate();
  const [user, setUser] = useState(usernm);
  const [account, setAccount] = useState(accnt);
  const [homeAcess, setHomeAcess] = useState(homeacs);
  const [isAuthenticated, setIsAuthenticated] = useState(!!usernm);
  const [hasShortToken, setHasShortToken] = useState<boolean>(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState<string>('');

  useEffect(() => {
    if (token) {
      setUser(usernm);
      setAccount(accnt);
      setHomeAcess(homeacs);
    }
  }, []);

  async function getShortToken(shortToken: string) {
    changeVisibility(true);
    let shtToken = false;
    const promises: Promise<unknown>[] = [];
    promises.push(
      api
        .post(`/accounts/${shortToken}`)
        .then((response: { data: { shortToken: boolean } }) => {
          shtToken = response.data.shortToken;
          setHasShortToken(response.data.shortToken);
          changeVisibility(false);
        })
        .catch((error) => {
          errorTreatment(error);
          changeVisibility(false);
        })
    );
    await Promise.all(promises);
    return shtToken;
  }

  async function signIn({ code, username, password }: SignInCredentials) {
    changeVisibility(true);
    setLoginErrorMessage('')
    api
      .post(`/accounts/${code}/authenticate`, { username, password })
      .then((response: Login) => {
        const token = response.data.token;
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        setCookie(undefined, "controlTower.token", token, {
          maxAge: 60 * 60 * 24, //7 dias
          path: "/",
        });

        setCookie(undefined, "controlTower.username", username, {
          maxAge: 60 * 60 * 24, //7 dias
          path: "/",
        });

        setCookie(undefined, "controlTower.account", response.data.account, {
          maxAge: 60 * 60 * 24, //7 dias
          path: "/",
        });

        setCookie(undefined, "controlTower.code", code, {
          maxAge: 60 * 60 * 24, //7 dias
          path: "/",
        });

        setCookie(undefined, "controlTower.homeAcess", "1", {
          maxAge: 60 * 60 * 24, //7 dias
          path: "/",
        });

        setCookie(undefined, "controlTower.email",  response.data.email, {
          maxAge: 60 * 60 * 24, //7 dias
          path: "/",
        });

        setAccount(response.data.account);
        setHomeAcess("1");
        setUser(username);
        setIsAuthenticated(true);
        changeVisibility(false);
        navigate("/");
      })
      .catch((error) => {
        changeVisibility(false);
        if (error.response.data?.args[0] === 'S1008') {
          setLoginErrorMessage(error.response.data.message)
        } else {
          toast.error(
            (error.response.data.message as string) ?? "Credencias Inválidas",
            {
              position: toast.POSITION.TOP_RIGHT,
            }
          );
        }
      });
  }

  //FUNÇÃO DE LOGIN PELA SESSION DO CONSOLE
  async function memcacheLogin(token: string) {
    changeVisibility(true);
    destroyCookie(undefined, "controlTower.context", { path: "/" });
    api
      .post(`/authenticate/memcache`, { token: token })
      .then((response: LoginMemcache) => {
        const token = response.data.token;
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        setCookie(undefined, "controlTower.token", token, {
          maxAge: 60 * 60 * 24, //7 dias
          path: "/",
        });

        setCookie(undefined, "controlTower.username", response.data.username, {
          maxAge: 60 * 60 * 24, //7 dias
          path: "/",
        });

        setCookie(
          undefined,
          "controlTower.account",
          response.data.account_name,
          {
            maxAge: 60 * 60 * 24, //7 dias
            path: "/",
          }
        );

        setCookie(undefined, "controlTower.code", response.data.account_code, {
          maxAge: 60 * 60 * 24, //7 dias
          path: "/",
        });

        setCookie(undefined, "controlTower.homeAcess", "1", {
          maxAge: 60 * 60 * 24, //7 dias
          path: "/",
        });

        setCookie(undefined, "controlTower.email", response.data.email, {
          maxAge: 60 * 60 * 24, //7 dias
          path: "/",
        });

        setAccount(response.data.account_name);
        setHomeAcess("1");
        setUser(response.data.username);
        setIsAuthenticated(true);
        changeVisibility(false);
        window.location.reload();
      })
      .catch((error) => {
        changeVisibility(false);
        if (error.response.data.args[0] === 'S1008') {
          setCookie(undefined, "controlTower.context", error.response.data.message, {
            maxAge: 60 * 60 * 24, //7 dias
            path: "/",
          });
        } else {
          toast.error(
            (error.response.data.message as string) ?? "Credencias Inválidas",
            {
              position: toast.POSITION.TOP_RIGHT,
            }
          );
        }
        navigate("/login");
      });
  }

  //FUNÇÃO DE LOGOUT
  async function signOut() {
    destroyCookie(undefined, "controlTower.token", { path: "/" });
    destroyCookie(undefined, "controlTower.username", { path: "/" });
    destroyCookie(undefined, "controlTower.account", { path: "/" });
    destroyCookie(undefined, "controlTower.code", { path: "/" });
    destroyCookie(undefined, "controlTower.homeAcess", { path: "/" });
    destroyCookie(undefined, "controlTower.email", { path: "/" });
    setIsAuthenticated(false);
    navigate("/login");
  }

  return (
    <AuthContext.Provider
      value={{
        signIn,
        isAuthenticated,
        user,
        signOut,
        account,
        homeAcess,
        memcacheLogin,
        hasShortToken,
        getShortToken,
        loginErrorMessage
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
